import { Playbook } from '../types';
import { breakthroughCycles } from '@/data/playbookCyclesData';

// This file contains pre-transformed playbook data ready for production use
// Instead of transforming the data at runtime, we use this pre-transformed data
// which matches exactly what the UI expects

// Execute the transformation once to get the result
function transformPlaybookDataOnce() {
  // Mix of real avatars and generated ones
  const avatarPools = [
    // Pool 1 - First playbook
    [
      'https://randomuser.me/api/portraits/women/12.jpg',
      'https://randomuser.me/api/portraits/men/32.jpg',
      'https://ui-avatars.com/api/?name=Emma+Wilson&background=0D8ABC&color=fff',
      'https://randomuser.me/api/portraits/women/44.jpg',
      'https://ui-avatars.com/api/?name=David+Chen&background=6f42c1&color=fff',
    ],
    // Pool 2 - Second playbook
    [
      'https://randomuser.me/api/portraits/men/22.jpg',
      'https://ui-avatars.com/api/?name=Sophia+Lee&background=28a745&color=fff',
      'https://randomuser.me/api/portraits/women/29.jpg',
      'https://ui-avatars.com/api/?name=Noah+Brown&background=fd7e14&color=fff',
      'https://randomuser.me/api/portraits/men/45.jpg',
    ],
    // Pool 3 - Third playbook
    [
      'https://ui-avatars.com/api/?name=Olivia+Smith&background=dc3545&color=fff',
      'https://randomuser.me/api/portraits/women/67.jpg',
      'https://randomuser.me/api/portraits/men/67.jpg',
      'https://ui-avatars.com/api/?name=Liam+Garcia&background=0D8ABC&color=fff',
      'https://randomuser.me/api/portraits/women/54.jpg',
    ],
  ];
  
  // Stock images from Unsplash related to growth, business, and startup concepts
  const cardImages = [
    'https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=1200&q=80', // Modern startup business graph
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80', // Team collaboration
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80', // Marketing strategy planning
    'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&w=1200&q=80', // Content planning
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80', // Digital marketing
  ];
  
  const detailImages = [
    'https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=1200&q=80', // Modern startup business graph
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80', // Team collaboration
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80', // Marketing strategy planning
    'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&w=1200&q=80', // Content planning
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80', // Digital marketing
  ];
  
  /**
   * Utility to generate a unique set of user avatars and names for each playbook
   */
  function generateUniqueAvatars(playbookIndex: number, totalPlaybooks: number, count: number = 5) {
    // Pools of names for initials
    const firstNames = [
      'Alex', 'Maria', 'Sam', 'Jordan', 'Jamie', 'Emma', 'David', 'Sophia', 'Noah', 'Olivia',
      'Liam', 'Ava', 'Mason', 'Ella', 'Lucas', 'Mia', 'Ethan', 'Amelia', 'Logan', 'Harper',
      'Jacob', 'Emily', 'Michael', 'Abigail', 'Benjamin', 'Charlotte', 'Elijah', 'Madison',
      'James', 'Scarlett', 'William', 'Grace', 'Henry', 'Chloe', 'Jack', 'Penelope', 'Owen', 'Layla',
      'Leo', 'Zoe', 'Isaac', 'Lily', 'Sebastian', 'Nora', 'Mateo', 'Hazel', 'Jack', 'Ellie',
    ];
    const lastNames = [
      'Johnson', 'Garcia', 'Taylor', 'Lee', 'Smith', 'Chen', 'Brown', 'Davis', 'Martinez', 'Clark',
      'Lewis', 'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Hill', 'Green', 'Adams',
      'Baker', 'Nelson', 'Carter', 'Mitchell', 'Perez', 'Roberts', 'Turner', 'Phillips', 'Campbell', 'Parker',
      'Evans', 'Edwards', 'Collins', 'Stewart', 'Sanchez', 'Morris', 'Rogers', 'Reed', 'Cook', 'Morgan',
    ];
    // To avoid overlap, offset the name/portrait index by playbookIndex
    const avatars = [];
    for (let i = 0; i < count; i++) {
      // Alternate between real and generated avatars
      if (i % 2 === 0) {
        // Real photo from randomuser.me
        // Use a unique index for each playbook/avatar
        const gender = (playbookIndex + i) % 2 === 0 ? 'men' : 'women';
        const userIndex = (playbookIndex * count + i * 7) % 99; // 0-98
        avatars.push(`https://randomuser.me/api/portraits/${gender}/${userIndex}.jpg`);
      } else {
        // Generated avatar with unique initials
        const nameIndex = (playbookIndex * count + i * 13) % firstNames.length;
        const lastNameIndex = (playbookIndex * count + i * 17) % lastNames.length;
        const name = `${firstNames[nameIndex]}+${lastNames[lastNameIndex]}`;
        const bg = ['0D8ABC', '28a745', 'dc3545', 'fd7e14', '6f42c1', '343a40'][((playbookIndex + i) % 6)];
        avatars.push(`https://ui-avatars.com/api/?name=${name}&background=${bg}&color=fff`);
      }
    }
    return avatars;
  }
  
  return breakthroughCycles.map((cycle, index) => {
    // Clean the title (remove "Breakthrough Cycle X: " prefix)
    let title = cycle.title.replace(/^Breakthrough Cycle \d+:\s*/i, '');
    
    // Override titles, descriptions, and outcomes for specific playbooks
    let customShortDescription: string | null = null;
    let customOutcomes: string[] | null = null;
    
    // Map old titles to new ones based on cycle ID or content
    if (cycle.id === "cycle-getting-engine-started" || title.includes("Getting The Engine Started")) {
      title = "Get Your First 20 Users Roadmap";
      customShortDescription = "A guided, step-by-step plan to go from 0 to 20 users, even if you have no audience or marketing experience.";
      customOutcomes = ["New users", "Get feedback & reviews"];
    } else if (cycle.id === "cycle-cold-outreach" || title.includes("Cold Outreach")) {
      title = "Turn Comments & DMs into Users";
      customShortDescription = "Master the art of engaging in comments and DMs to convert interest into users. Learn subtle sales psychology, messaging frameworks, and ethical templates that work.";
      customOutcomes = ["New users", "Improve sales skills"];
    } else if (cycle.id === "cycle-building-momentum" || title.includes("Building Momentum")) {
      title = "How To Build In Public";
      customShortDescription = "Gain visibility and trust by consistently documenting progress, sharing updates in communities and public logs.";
      customOutcomes = ["Increase awareness & trust", "Gain followers"];
    } else if (cycle.id === "cycle-written-content-cluster" || title.includes("Written Content Cluster")) {
      title = "Organic Growth With Blogs & Social Media Posts";
      customShortDescription = "Rank on search engines like Google to gain organic traffic, authority and win new users in the long run. Use social media posts to gain awareness and trust.";
      customOutcomes = ["Long-term (slower) user acquisition", "Improved social media presence"];
    } else if (cycle.id === "cycle-lead-magnet-email-engine" || title.includes("Lead Magnet & Email Engine")) {
      title = "Create a Lead Magnet & Email Sequence";
      customShortDescription = "Create a free value asset to attract potential users, in exchange for their contact information. Followed by value & conversion email sequences.";
      customOutcomes = ["Lead magnet", "Email sequence to gain new users"];
    } else if (cycle.id === "cycle-video-content-cluster" || title.includes("Video Content Cluster")) {
      title = "Go Viral With Video Content";
      customShortDescription = "Build trust, explain the product, and expand visibility with high-leverage video content.";
      customOutcomes = ["UGC videos", "Potential viral videos"];
    } else if (cycle.id === "cycle-paid-acquisition-partnership" || title.includes("Paid Acquisition & Partnership Engine")) {
      title = "Advanced Scaling Strategies";
      customShortDescription = "Rapidly scale user growth with high-leverage user acquisition strategies like paid advertising, affiliates and partnerships.";
      customOutcomes = ["Gain users through other's networks"];
    } else if (cycle.id === "cycle-retention-expansion" || title.includes("Retention & Expansion Engine")) {
      title = "Increase Retention & Revenue Per User";
      customShortDescription = "Give users more reason to stay and increase the value of each user with retention and upselling strategies";
      customOutcomes = ["Increase retention", "Increased revenue per user"];
    } else if (cycle.id === "cycle-viral-amplification" || title.includes("Viral Amplification Engine")) {
      title = "Viral Launch Strategies";
      customShortDescription = "Drive a major spike in visibility, trust, and signups through a high-impact public launch.";
      customOutcomes = ["High ranking on platforms like Product Hunt", "Increased authority", "Increased competitive advantage"];
    }
    
    // Transform cycle.targets (string[]) into PlaybookKeyTarget[]
    const keyTargets = cycle.targets.map(target => {
      // Extract the first part before the colon as the title, if it exists
      const colonIndex = target.indexOf(':');
      let title = '';
      let description = target;
      
      if (colonIndex > 0) {
        title = target.substring(0, colonIndex).trim();
        description = target.substring(colonIndex + 1).trim();
      } else {
        // If no colon, use the first few words as the title
        const words = target.split(' ');
        if (words.length > 3) {
          title = words.slice(0, 3).join(' ');
        } else {
          title = target;
        }
      }
      
      return {
        title,
        description
      };
    });
    
    // Transform cycle.phases (GrowthPhase[]) into Phase[]
    const phases = cycle.phases.map(phase => {
      // Transform phase.tasks, tips, and ongoingTasks into Task[]
      const tasks = [];
      
      // Add regular tasks
      phase.tasks.forEach(task => {
        tasks.push({
          id: task.id,
          title: task.description ? task.description.replace(/^Task \d+ -\s*/i, '') : 'Task',
          details: task.innerContent,
          isCompleted: false
        });
      });
      
      // Add tips as special tasks
      if (phase.tips) {
        phase.tips.forEach(tip => {
          tasks.push({
            id: tip.id,
            title: 'Pro Tip',
            hint: tip.content,
            isCompleted: false
          });
        });
      }
      
      // Add ongoing daily tasks
      if (phase.ongoingDailyTasks) {
        phase.ongoingDailyTasks.forEach(task => {
          tasks.push({
            id: task.id,
            title: `Daily: ${task.title || task.description}`,
            details: task.description,
            isCompleted: false
          });
        });
      }
      
      // Add ongoing non-daily tasks
      if (phase.ongoingNonDailyTasks) {
        phase.ongoingNonDailyTasks.forEach(task => {
          tasks.push({
            id: task.id,
            title: `Ongoing: ${task.title || task.description}`,
            details: task.description,
            isCompleted: false
          });
        });
      }
      
      return {
        id: phase.id,
        title: phase.title,
        description: phase.introduction,
        keyTargets: phase.keyTargets,
        tasks,
        progress: 0 // Initial progress is 0
      };
    });
    
    // Use custom outcomes if provided, otherwise derive them
    const outcomes = customOutcomes || deriveOutcomes(cycle);
    const newSkills = deriveNewSkills(cycle);
    
    // Use custom short description if provided, otherwise create from goals
    const shortDescription = customShortDescription || createShortDescription(cycle.goals);
    
    // Count total tasks
    const totalTasks = phases.reduce((sum, phase) => sum + phase.tasks.length, 0);
    
    // Use the stock image arrays to get an image for this playbook, cycling through if needed
    const cardImage = cardImages[index % cardImages.length];
    const detailImage = detailImages[index % detailImages.length];
    
    // Define base enrollment count for each playbook
    const baseEnrollmentCounts = {
      0: 94, // First playbook: 94 ± 10
      1: 57, // Second playbook: 57 ± 10
      2: 52, // Third playbook: 52 ± 10
    };
    
    // Get the base count for this playbook or use a default
    const baseCount = baseEnrollmentCounts[index] || 50;
    
    // Generate a random variation within ±10 of the base count
    const enrollmentVariation = Math.floor(Math.random() * 21) - 10; // Random number between -10 and +10
    const totalEnrolledUsers = baseCount + enrollmentVariation;
    
    // Generate unique avatars for this playbook
    const enrolledUserAvatars = generateUniqueAvatars(index, breakthroughCycles.length, 5);
    
    return {
      id: cycle.id,
      title,
      cardImage,
      detailImage,
      card_image_url: cardImage,
      detail_image_url: detailImage,
      badge: undefined, // No badges for now
      shortDescription,
      outcomes,
      newSkills,
      enrolledUserAvatars,
      totalEnrolledUsers,
      introduction: cycle.goals,
      keyTargets,
      phases,
      overallProgress: 0, // Initial progress is 0
      totalTasks,
      completedTasks: 0, // Initial completed tasks is 0
      display_order: index // Add display_order based on the index in the array
    };
  });
}

/**
 * Creates a shorter description from the goals text
 */
function createShortDescription(goals: string): string {
  // Take the first sentence or first 150 characters
  const firstSentenceMatch = goals.match(/^[^.!?]+[.!?]/);
  if (firstSentenceMatch) {
    return firstSentenceMatch[0];
  }
  
  return goals.length > 150 ? `${goals.substring(0, 147)}...` : goals;
}

/**
 * Derives outcome bullet points from the cycle content
 */
function deriveOutcomes(cycle: any): string[] {
  // This is a simplified approach - in a real implementation, 
  // you might use NLP or more sophisticated text analysis
  
  // Extract key phrases from goals that indicate outcomes
  const outcomes: string[] = [];
  
  // Look for phrases like "you will..." or "users will..." in the goals
  const goalPhrases = cycle.goals.split(/[.!?]+/);
  for (const phrase of goalPhrases) {
    if (
      phrase.toLowerCase().includes('will get') ||
      phrase.toLowerCase().includes('will have') ||
      phrase.toLowerCase().includes('will be able to')
    ) {
      const cleanedPhrase = phrase.trim();
      if (cleanedPhrase && !outcomes.includes(cleanedPhrase)) {
        outcomes.push(cleanedPhrase);
      }
    }
  }
  
  // If we couldn't extract enough outcomes, create some generic ones based on the title
  if (outcomes.length < 3) {
    const title = cycle.title.replace(/^Breakthrough Cycle \d+:\s*/i, '').toLowerCase();
    
    if (title.includes('engine') || title.includes('start')) {
      outcomes.push('Steady lead flow from targeted DMs and emails');
      outcomes.push('Organic inbound traffic from your first SEO-optimized blog');
    } else if (title.includes('outreach')) {
      outcomes.push('Surface top 3 churn blockers from drop-off users');
      outcomes.push('Pinpoint your "aha" moment with new user interviews');
    } else if (title.includes('content') || title.includes('momentum')) {
      outcomes.push('10 SEO-focused blog posts live and indexed');
      outcomes.push('Internal links added to boost crawlability and session depth');
    }
    
    // Add a generic outcome about feedback
    outcomes.push('Actionable feedback to refine your product roadmap');
    outcomes.push('A repeatable framework for driving and retaining new users');
  }
  
  // Limit to 4 outcomes
  return outcomes.slice(0, 4);
}

/**
 * Derives new skills bullet points from the cycle content
 */
function deriveNewSkills(cycle: any): string[] {
  // This is a simplified approach - in a real implementation, 
  // you might use NLP or more sophisticated text analysis
  
  const skills: string[] = [];
  const title = cycle.title.replace(/^Breakthrough Cycle \d+:\s*/i, '').toLowerCase();
  
  // Create skills based on the actual cycle content and what users learn
  if (cycle.id === "cycle-getting-engine-started") {
    skills.push('Profile optimization');
    skills.push('Community outreach');
    skills.push('Social proof creation');
    skills.push('Direct messaging');
  } else if (cycle.id === "cycle-cold-outreach") {
    skills.push('Sales psychology');
    skills.push('Conversion messaging');
    skills.push('User feedback analysis');
    skills.push('Referral systems');
  } else if (cycle.id === "cycle-building-momentum") {
    skills.push('Build-in-public strategy');
    skills.push('Audience engagement');
    skills.push('Content distribution');
    skills.push('Community building');
  } else if (cycle.id === "cycle-written-content-cluster") {
    skills.push('SEO blog writing');
    skills.push('Content marketing');
    skills.push('Organic traffic growth');
    skills.push('Social media posting');
  } else if (cycle.id === "cycle-lead-magnet-email-engine") {
    skills.push('Lead magnet creation');
    skills.push('Email automation');
    skills.push('Landing page optimization');
    skills.push('Conversion funnels');
  } else if (cycle.id === "cycle-video-content-cluster") {
    skills.push('Video content creation');
    skills.push('UGC collection');
    skills.push('Viral video strategies');
    skills.push('Platform optimization');
  } else if (cycle.id === "cycle-paid-acquisition-partnership") {
    skills.push('Meta ads management');
    skills.push('Performance marketing');
    skills.push('Partnership development');
    skills.push('Scaling strategies');
  } else if (cycle.id === "cycle-retention-expansion") {
    skills.push('Customer retention');
    skills.push('Revenue optimization');
    skills.push('User lifecycle management');
    skills.push('Upselling techniques');
  } else if (cycle.id === "cycle-viral-amplification") {
    skills.push('Launch campaign planning');
    skills.push('Product Hunt optimization');
    skills.push('PR & media outreach');
    skills.push('Launch distribution');
  } else {
    // Default skills
    skills.push('Growth strategy');
    skills.push('User acquisition');
    skills.push('Content creation');
    skills.push('Analytics interpretation');
  }
  
  return skills;
}

// Pre-transform the data once and export it
export const transformedPlaybooks: Playbook[] = transformPlaybookDataOnce(); 